import React, { useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLock,
  faTimes,
  faCrown,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { ContentContext } from '../../context/ContentProvider';
import { PAYWALL_COLUMNS } from '../../../app_constant';

const PremiumDialog = ({ 
  isOpen, 
  onClose, 
  onUpgrade, 
  theme 
}) => {
  const { examStats, totals } = useContext(ContentContext);
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2rem',
      maxWidth: '450px',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    iconWrapper: {
      width: '4rem',
      height: '4rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[400]}, ${theme?.primary?.orange?.[600]})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
      fontSize: '2rem',
      color: 'white',
      position: 'relative',
    },
    lockOverlay: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '1.5rem',
      color: 'white',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      marginBottom: '1.5rem',
      lineHeight: '1.5',
    },
    features: {
      marginBottom: '2rem',
    },
    feature: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      marginBottom: '0.75rem',
      fontSize: '0.875rem',
      color: theme?.text?.primary,
      lineHeight: '1.5',
    },
    checkIcon: {
      color: theme?.primary?.green?.[500],
      fontSize: '0.875rem',
      marginTop: '0.125rem',
      flexShrink: 0,
    },
    highlightedText: {
      color: theme?.primary?.blue?.[500] || '#0973ba',
      fontWeight: '600',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
    },
    button: {
      flex: 1,
      padding: '0.875rem 1.5rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    secondaryButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary || '#e5e7eb'}`,
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[500]}, ${theme?.primary?.orange?.[600]})`,
      color: 'white',
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    },
  };

  

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <motion.div
              style={styles.iconWrapper}
              animate={{ 
                scale: [1, 1.1, 1],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3,
              }}
            >
              <FontAwesomeIcon icon={faLock} />
              
            </motion.div>
            
            <h2 style={styles.title}>Premium Access Required</h2>
            
            <p style={styles.subtitle}>
              Upgrade to unlock unlimited access and accelerate your exam preparation.
            </p>
            
            <div style={styles.features}>
              {/* Feature 1: Unlimited access to exams */}
              <motion.div
                style={styles.feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
                <span>
                  Unlimited access to{' '}
                  {examStats && examStats.length > 0 ? (
                    examStats.length === 1 ? (
                      <>
                        <span style={styles.highlightedText}>{examStats[0].title}</span>
                        {' exam prep'}
                      </>
                    ) : examStats.length === 2 ? (
                      <>
                        <span style={styles.highlightedText}>{examStats[0].title}</span>
                        {' and '}
                        <span style={styles.highlightedText}>{examStats[1].title}</span>
                        {' exam prep'}
                      </>
                    ) : (
                      <>
                        {examStats.map((exam, index) => (
                          <React.Fragment key={exam.key}>
                            <span style={styles.highlightedText}>{exam.title}</span>
                            {index < examStats.length - 2 && ', '}
                            {index === examStats.length - 2 && ', and '}
                          </React.Fragment>
                        ))}
                        {' exam prep'}
                      </>
                    )
                  ) : (
                    <span style={styles.highlightedText}>exam prep</span>
                  )}
                </span>
              </motion.div>

              {/* Feature 2: Practice questions */}
              <motion.div
                style={styles.feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
                <span>
                  <span style={styles.highlightedText}>
                    {totals?.questions ? `${totals.questions.toLocaleString()}` : '1,000+'}
                  </span>{' '}
                  practice questions with detailed explanations
                </span>
              </motion.div>

              {/* Feature 3: Additional content (configurable via PAYWALL_COLUMNS) */}
              <motion.div
                style={styles.feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
                {PAYWALL_COLUMNS ? (
                  <span>
                    {PAYWALL_COLUMNS.filter(col => col.key !== 'questions').map((col, index, arr) => (
                      <React.Fragment key={col.key}>
                        {index > 0 && (index === arr.length - 1 ? ' and ' : ', ')}
                        <span style={styles.highlightedText}>
                          {totals?.[col.key] ? totals[col.key].toLocaleString() : ''}
                        </span>{' '}
                        {col.label.charAt(0) + col.label.slice(1).toLowerCase()}
                      </React.Fragment>
                    ))}
                    {' '}across all exams
                  </span>
                ) : totals?.flashcards > 0 || totals?.articles > 0 ? (
                  <span>
                    <span style={styles.highlightedText}>
                      {totals.flashcards.toLocaleString()}
                    </span>{' '}
                    flashcards and{' '}
                    <span style={styles.highlightedText}>
                      {totals.articles.toLocaleString()}
                    </span>{' '}
                    study articles
                  </span>
                ) : (
                  <span>
                    <span style={styles.highlightedText}>
                      {totals?.practiceTests ? totals.practiceTests.toLocaleString() : '36'}
                    </span>{' '}
                    Practice Tests across all exams
                  </span>
                )}
              </motion.div>

              {/* Feature 4: Readiness Score */}
              <motion.div
                style={styles.feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
                <span>
                  Track exam readiness with{' '}
                  <span style={styles.highlightedText}>Readiness Score</span>
                </span>
              </motion.div>
            </div>
            
            <div style={styles.buttonContainer}>
              <motion.button
                style={{ ...styles.button, ...styles.secondaryButton }}
                whileHover={styles.buttonHover}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
              >
                Maybe Later
              </motion.button>
              <motion.button
                style={{ ...styles.button, ...styles.primaryButton }}
                whileHover={styles.buttonHover}
                whileTap={{ scale: 0.95 }}
                onClick={onUpgrade}
              >
                <FontAwesomeIcon icon={faCrown} />
                View Plans
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PremiumDialog; 